import { useEffect, useMemo, useState } from 'react';

import { useInfiniteScroll } from '@/hooks/use-infinite-scroll';
import type { ConnectorInvocationStatus } from '@/types/core/connector-invocation-status';
import type { UUID } from '@/utils/uuid';

import { isActiveInvocationStatus } from '../../../components/tenants/connectors/run-status-config';
import { useListConnectorFeatureInvocationStatuses } from './use-list-connector-feature-invocation-statuses';

type UseConnectorRunsStateOptions = {
  tenantId?: UUID;
  connectorId?: UUID;
  featureId?: UUID;
  liveStatus: ConnectorInvocationStatus | null | undefined;
  onRetry?: () => void;
  /** The run sequence number driven by the URL (?run=N). When set, this overrides the default first-run selection. */
  selectedRunSequence?: number;
  /** Called when the user selects or deselects a run, so the parent can sync the URL param. */
  onRunSelect?: (seq: number | null) => void;
};

export type ConnectorRunsState = {
  mergedRuns: ConnectorInvocationStatus[];
  /** True while the initial run-history page is still loading (no data yet). */
  isLoadingRuns: boolean;
  effectiveRun: ConnectorInvocationStatus | null;
  effectiveSequence: number | undefined;
  isShowingLiveRun: boolean;
  isDefaultSelection: boolean;
  isMobile: boolean;
  mobileModalOpen: boolean;
  setMobileModalOpen: (open: boolean) => void;
  isFetchingNextPage: boolean;
  sentinelRef: React.RefObject<HTMLDivElement | null>;
  handleRetry: () => void;
  handleRowClick: (run: ConnectorInvocationStatus, index: number) => void;
};

export function useConnectorRunsState({
  tenantId,
  connectorId,
  featureId,
  liveStatus,
  onRetry,
  selectedRunSequence,
  onRunSelect,
}: UseConnectorRunsStateOptions): ConnectorRunsState {
  const [mobileModalOpen, setMobileModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia('(max-width: 639px)').matches
  );

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 639px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingRuns,
  } = useListConnectorFeatureInvocationStatuses(
    tenantId,
    connectorId,
    featureId,
    {
      limit: 20,
      enabled: !!tenantId && !!connectorId && !!featureId,
    }
  );

  const sentinelRef = useInfiniteScroll({
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  });

  const runs = useMemo(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data]
  );

  const mergedRuns = useMemo(() => {
    let merged: ConnectorInvocationStatus[];
    if (!liveStatus) {
      merged = runs;
    } else {
      // Match by runSequence when available, fall back to requestedAt — list items
      // don't carry run_sequence from the API so requestedAt is the only stable key.
      const existingIndex = runs.findIndex((r) =>
        liveStatus.runSequence !== undefined && r.runSequence !== undefined
          ? r.runSequence === liveStatus.runSequence
          : liveStatus.requestedAt !== undefined &&
            r.requestedAt?.getTime() === liveStatus.requestedAt.getTime()
      );
      if (existingIndex >= 0) {
        if (!isActiveInvocationStatus(liveStatus)) {
          merged = runs;
        } else {
          // Replace the stale historical entry with the live one so the row stays current.
          const updated = [...runs];
          updated[existingIndex] = liveStatus;
          merged = updated;
        }
      } else {
        // Prepend liveStatus even when terminal: history hasn't refreshed yet,
        // so keep it visible until the invalidated query comes back.
        merged = [liveStatus, ...runs];
      }
    }

    // Deduplicate: prefer runSequence as key, fall back to requestedAt ms.
    // This guards against overlap between the live status and list results
    // since list items don't carry run_sequence from the API.
    const seenSeq = new Set<number>();
    const seenTs = new Set<number>();
    return merged.filter((r) => {
      if (r.runSequence !== undefined) {
        if (seenSeq.has(r.runSequence)) return false;
        seenSeq.add(r.runSequence);
        return true;
      }
      const ts = r.requestedAt?.getTime();
      if (ts === undefined) return true;
      if (seenTs.has(ts)) return false;
      seenTs.add(ts);
      return true;
    });
  }, [runs, liveStatus]);

  const liveSequence = liveStatus?.runSequence;

  // If the selected run (by URL) is the live run, use liveStatus so the detail card stays live-updating.
  // Falls back to the first run in the merged list when no URL param is set.
  const effectiveRun = useMemo(() => {
    if (selectedRunSequence !== undefined) {
      const found = mergedRuns.find(
        (r) => r.runSequence === selectedRunSequence
      );
      if (found) {
        return liveStatus && found.runSequence === liveSequence
          ? liveStatus
          : found;
      }
    }
    return mergedRuns[0] ?? null;
  }, [selectedRunSequence, mergedRuns, liveStatus, liveSequence]);

  const effectiveSequence = useMemo(() => {
    if (!effectiveRun) return undefined;
    if (effectiveRun.runSequence !== undefined) return effectiveRun.runSequence;
    const idx = mergedRuns.indexOf(effectiveRun);
    return idx >= 0 ? mergedRuns.length - idx : undefined;
  }, [effectiveRun, mergedRuns]);

  // Forward live props only when the detail card is showing the live run.
  const isShowingLiveRun =
    liveStatus !== null &&
    liveStatus !== undefined &&
    (effectiveSequence !== undefined
      ? effectiveSequence === liveSequence
      : effectiveRun === liveStatus);

  function handleRetry() {
    onRunSelect?.(null);
    onRetry?.();
  }

  function handleRowClick(run: ConnectorInvocationStatus, index: number) {
    const seq = run.runSequence ?? mergedRuns.length - index;
    const defaultSeq = mergedRuns[0]?.runSequence;
    // Clicking the default row when nothing is explicitly selected is a no-op.
    if (selectedRunSequence === undefined && seq === defaultSeq) {
      if (isMobile) setMobileModalOpen(true);
      return;
    }
    // Clicking the already-selected row resets to default.
    if (selectedRunSequence !== undefined && seq === selectedRunSequence) {
      onRunSelect?.(null);
    } else {
      onRunSelect?.(seq);
    }
    if (isMobile) setMobileModalOpen(true);
  }

  return {
    mergedRuns,
    isLoadingRuns,
    effectiveRun,
    effectiveSequence,
    isShowingLiveRun,
    isDefaultSelection: selectedRunSequence === undefined,
    isMobile,
    mobileModalOpen,
    setMobileModalOpen,
    isFetchingNextPage,
    sentinelRef,
    handleRetry,
    handleRowClick,
  };
}
